import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import { AdminNav } from '../../components/AdminNav';

export const AdminIndividualAuctioneers = () => {
  const [auctioneers, setAuctioneers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const { token, isAuthenticated } = useAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }

    fetchStats();
    fetchAuctioneers();
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAuctioneers();
    }
  }, [search, stateFilter, verifiedFilter]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/individual-auctioneers/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchAuctioneers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', 100);
      if (search) params.append('search', search);
      if (stateFilter) params.append('state', stateFilter);
      if (verifiedFilter) params.append('verified', verifiedFilter);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/individual-auctioneers?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch auctioneers');

      const data = await response.json();
      setAuctioneers(data.auctioneers);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (auctioneerId, verified) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/individual-auctioneers/${auctioneerId}/verify?verified=${verified}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to update verification');

      setSuccess(`Auctioneer ${verified ? 'verified' : 'unverified'} successfully`);
      fetchAuctioneers();
      fetchStats();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (auctioneerId, name) => {
    if (!window.confirm(`Are you sure you want to delete ${name}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/individual-auctioneers/${auctioneerId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to delete auctioneer');

      setSuccess('Auctioneer deleted successfully');
      fetchAuctioneers();
      fetchStats();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Manage Individual Auctioneers</h2>

        {success && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded">
            <p className="text-green-700">{success}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4 border-t-4 border-green-500">
              <div className="text-sm font-medium text-gray-600">Total Auctioneers</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{stats.total_auctioneers}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-t-4 border-blue-500">
              <div className="text-sm font-medium text-gray-600">Verified</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{stats.verified}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-t-4 border-yellow-500">
              <div className="text-sm font-medium text-gray-600">Claimed</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{stats.claimed}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-t-4 border-purple-500">
              <div className="text-sm font-medium text-gray-600">Quote Requests</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{stats.quote_requests?.total || 0}</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                placeholder="Name, city, or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <select
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">All States</option>
                <option value="OH">Ohio</option>
                <option value="PA">Pennsylvania</option>
                <option value="TX">Texas</option>
                <option value="NY">New York</option>
                <option value="CA">California</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Verified Status</label>
              <select
                value={verifiedFilter}
                onChange={(e) => setVerifiedFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">All</option>
                <option value="true">Verified Only</option>
                <option value="false">Unverified Only</option>
              </select>
            </div>
          </div>
        </div>

        {/* Auctioneers Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading auctioneers...</p>
          </div>
        ) : auctioneers.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <span className="text-6xl mb-4 block">🔍</span>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Auctioneers Found</h3>
            <p className="text-gray-600">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Specialties
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {auctioneers.map((auctioneer) => (
                    <tr key={auctioneer.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{auctioneer.name}</div>
                        {auctioneer.years_experience && (
                          <div className="text-xs text-gray-500">{auctioneer.years_experience}+ years exp</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {auctioneer.city}, {auctioneer.state}
                        </div>
                        <div className="text-xs text-gray-500">
                          {auctioneer.service_radius_miles} mi radius
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{auctioneer.email || 'No email'}</div>
                        <div className="text-xs text-gray-500">{auctioneer.phone || 'No phone'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {auctioneer.specialties?.slice(0, 2).map((spec, idx) => (
                            <span
                              key={idx}
                              className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                            >
                              {spec}
                            </span>
                          ))}
                          {auctioneer.specialties?.length > 2 && (
                            <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                              +{auctioneer.specialties.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          {auctioneer.verified ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              ✓ Verified
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Unverified
                            </span>
                          )}
                          {auctioneer.claimed && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Claimed
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                        {auctioneer.verified ? (
                          <button
                            onClick={() => handleVerify(auctioneer.id, false)}
                            className="text-yellow-600 hover:text-yellow-900 font-medium"
                          >
                            Unverify
                          </button>
                        ) : (
                          <button
                            onClick={() => handleVerify(auctioneer.id, true)}
                            className="text-green-600 hover:text-green-900 font-medium"
                          >
                            Verify
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(auctioneer.id, auctioneer.name)}
                          className="text-red-600 hover:text-red-900 font-medium"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-gray-50 px-6 py-3 text-sm text-gray-600">
              Showing {auctioneers.length} auctioneers
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
